import { IEasyEdit, IGridDefinition, IGridColumnDefinition, IControlDefinition } from "Grids/Interfaces";
import { Grid } from "./Grid";
import { IDataRow } from "Entities";
import { EditModes, GridControlTypes, NotificationTypes, CssClass, TextBlockCategory } from "Enums";
import { IControl } from "Controls/Interfaces";
import { EventArgs } from "Core/Events";
import { Navigation, CoreContext } from "Core";
import { ControlEventTypes } from "Core/Events/EventTypes";

import { GridSettings } from "Configuration";
import { CommandService, ExportService, TextBlockService, ComboboxService, GridDefinitionService } from "Service";
import { DataConstants } from "Constants";

/**
 * ## EasyEdit
 *
 * Showing data as table view with cell editing.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of an EasyEdit with the name GridName.
 * // In script:
 * @@grd:GridName@@
 *
 * // On page:
 * $dms("GridName")
 *
 * // First EasyEdit on page:
 * $dms(".dms-easy-edit")
 * ```
 */
export class EasyEdit extends Grid implements IEasyEdit {
    /**
     * @hidden
     */
    public static wrapperTemplate: string = require("./Templates/EasyEdit.html");

    private changedRows: { [rowIndex: number]: IDataRow } = {};
    private changedValues: { [rowIndex: number]: IDataRow } = {};
    private originalValues: { [rowIndex: number]: IDataRow } = {};

    private editFirstCell: boolean;

    /**
     * Create a new EasyEdit.
     * @param gridDefinition
     * @param gridSettings
     * @param comboboxService
     * @param commandService
     * @param exportService
     * @param textBlockService
     * @hidden
     */
    constructor(
        gridDefinition: IGridDefinition,
        textBlockCategory: string,
        gridSettings: GridSettings,
        comboboxService: ComboboxService,
        commandService: CommandService,
        exportService: ExportService,
        gridDefinitionService: GridDefinitionService,
        textBlockService: TextBlockService
    ) {
        super(
            gridDefinition,
            textBlockCategory,
            gridSettings,
            comboboxService,
            commandService,
            exportService,
            gridDefinitionService,
            textBlockService
        );
        this.gridDefinition.AddAllowed = false;
    }

    /**
     * Sets the jQuery-Element to initialize the grid.
     * @param element JQuery-Wrapper in which the grid is initialized.
     * @hidden
     */
    protected async setElementInternal(element: JQuery): Promise<void> {
        await super.setElementInternal(element);
        (<any>$(window)).bindFirst("beforeunload", (e: any) => {
            CoreContext.Instance.allowPageTokenRelease = true;

            if (this.hasChanges()) {
                CoreContext.Instance.allowPageTokenRelease = false;
                let message = this.textBlockService.get("EasyEditUnsavedChangesWindowUnloadHint", this.textBlockCategory);
                e = e || window.event;

                // For IE and Firefox
                if (e) {
                    e.returnValue = message;
                }

                return message;
            }
        });
        this._kendoElement.table.on("keydown", e => this.onKeyDown(e));

        // if checkbox labels are clicked, store the id and click them after the cell opened.
        let table = this._kendoElement.table;
        if (this._kendoElement.lockedTable) {
            table = table.add(this._kendoElement.lockedTable);
        }
        table.on("click", ".dms-checkbox-label", (e) => {
            let checkbox = $(e.target);
            this.triggerCheckboxId = Number(checkbox.attr("data-dms-checkboxid"));
        });

        this.jQueryElement.on("change", ".dms-columnselector", e => this.handleColumnSelectorChange(e));
        this.jQueryElement.on("change", ".dms-columnselector-header", e => this.handleColumnSelectorHeaderChange(e));

        this.jQueryElement.find(".dms-columnselector-header").prop("indeterminate", true);
    }

    /**
     * Eventhandler for when a columnselector checkbox is clicked.
     * @param e EventArgs.
     */
    private handleColumnSelectorHeaderChange(e: JQuery.ChangeEvent) {
        let sender = $(e.target);
        let controlId = Number(sender.attr("data-dms-id"));
        let isChecked = sender.is(":checked");
        let valueColumnName = this.controlDefinitionDict[controlId].ValueColumnName;

        let dataRows = this._kendoElement.dataSource.data();
        for (let i = 0, len = dataRows.length; i < len; i++) {
            let dataRow = dataRows[i];
            this.saveRow(dataRow, { [valueColumnName]: isChecked });
            dataRow[valueColumnName] = isChecked;
            // dataRow.set(valueColumnName, isChecked);
        }
        this._kendoElement.refresh();
    }

    /**
     * Eventhandler for when a columnselector checkbox is clicked.
     * @param e EventArgs.
     */
    private handleColumnSelectorChange(e: JQuery.ChangeEvent) {
        let sender = $(e.target);
        let controlId = Number(sender.attr("data-dms-id"));
        let tr = sender.closest("tr");
        let isChecked = sender.is(":checked");
        let dataRow = this._kendoElement.dataItem(tr);
        let valueColumnName = this.controlDefinitionDict[controlId].ValueColumnName;
        this.saveRow(dataRow, { [valueColumnName]: isChecked });
        dataRow[valueColumnName] = isChecked;
        // dataRow.set(valueColumnName, isChecked);

        let header = this.jQueryElement.find(`#dms-columnselector-header-${controlId}`);
        if (header) {
            header.prop("indeterminate", true);
        }
    }

    /**
     * Get options used to initialize the kendo grid.
     * @param textBlockCategory Name of the textblockCategory.
     * @hidden
     */
    protected async getGridOptions(): Promise<kendo.ui.GridOptions> {
        let gridOptions = await super.getGridOptions();
        let editable = <kendo.ui.GridEditable>gridOptions.editable;
        editable.mode = "incell";
        gridOptions.navigatable = true;
        gridOptions.save = e => this.onSave(e);

        return gridOptions;
    }

    private onKeyDown(e: JQuery.KeyDownEvent): void {
        if (e.keyCode === kendo.keys.TAB) {
            let current = this._kendoElement.current();
            if (!current.hasClass("dms-column-editable")) {
                let nextCell: JQuery;
                if (e.shiftKey) {
                    nextCell = current.prevAll(".dms-column-editable:first");
                    if (!nextCell.length) {
                        //search the next row
                        let prevRow = current.parent().prev();
                        nextCell = prevRow.children(".dms-column-editable:last");
                    }
                } else {
                    nextCell = current.nextAll(".dms-column-editable:first");
                    if (!nextCell.length) {
                        //search the next row
                        let nextRow = current.parent().next();
                        if (!nextRow.length) {
                            nextCell = this._kendoElement.tbody.find(".dms-column-editable:first");
                        } else {
                            nextCell = nextRow.children(".dms-column-editable:first");
                        }
                    }
                }
                this._kendoElement.current(nextCell);
                this._kendoElement.editCell(nextCell);
            }
        }
    }

    /**
     * Triggered when the current grid row is changed.
     * @param e EventArgs.
     * @hidden
     */
    // protected onChange(e: kendo.ui.GridChangeEvent) {
    //     console.log("onchange");
    //     //  this.disposeEditContext();
    // }

    /**
     * Gets the CssClass for a given ColumnDefinition.
     * @param columnDefinition ColumnDefinition
     * @hidden
     */
    protected getColumnCssClass(columnDefinition: IGridColumnDefinition): string {
        let cssClass = "";
        if (columnDefinition) {
            cssClass = super.getColumnCssClass(columnDefinition);

            if (this.isColumnEditable(columnDefinition, false)) {
                cssClass += " dms-column-editable";
            }
        }
        return cssClass;
    }

    /**
     * Event triggered after changing the value of a cell.
     * @param e EventArgs.
     * @hidden
     */
    protected onSave(e: kendo.ui.GridSaveEvent) {
        if (!this.editContext.checkRequiredFields()) {
            console.log("fields empty");
            e.preventDefault();
            // hack: kendo grid still closes the cell even if you preventDefault the event.
            (<any>this._kendoElement).editable.end = function() {
                return false;
            };
            setTimeout(
                () =>
                    ((<any>this._kendoElement).editable.end = function() {
                        return true;
                    }),
                100
            );
            return;
        }
        if (e.model.dirty) {
            this.saveRow(e.model, e.values);
        }
    }

    /**
     * Event is triggered when the user saves a cell.
     * @param row The original dataRow (changes were not yet applied).
     * @param values The list of changed values (in format columnName: value).
     */
    private saveRow(row: kendo.data.ObservableObject, values: any) {
        let rowIndex = row[DataConstants.ROW_INDEX_COLUMN_NAME];
        let changedValues = this.changedValues[rowIndex] || {};
        this.changedValues[rowIndex] = <IDataRow>$.extend(changedValues || {}, values);
        this.changedRows[rowIndex] = <IDataRow>(<any>row);

        // get the original values stored for this row.
        let originalValues = this.originalValues[rowIndex];
        let columnName = Object.keys(values)[0];

        // no original values were saved yet -> save the values.
        if (originalValues == undefined || !originalValues.hasOwnProperty(columnName)) {
            this.originalValues[rowIndex] = <IDataRow>$.extend(originalValues || {}, { [columnName]: row[columnName] });
        } else {
            // check if the new value is the same as the original value.
            let originalValue = originalValues[columnName];
            if (values[columnName] == originalValue) {
                // reset the changes.
                delete changedValues[columnName];

                // last change was reset -> delete the changed rows.
                if (Object.keys(changedValues).length == 0) {
                    delete this.changedValues[rowIndex];
                    delete this.changedRows[rowIndex];
                }
            }
        }
    }

    private isColumnEditable(columnDefinition: IGridColumnDefinition, isAdd: boolean) {
        for (let ctlIdx = 0, len = columnDefinition.Controls.length; ctlIdx < len; ctlIdx++) {
            let controlDefinition = columnDefinition.Controls[ctlIdx];

            let editable =
                (controlDefinition.OnAddEditable && isAdd) ||
                (controlDefinition.OnEditEditable &&
                    !isAdd &&
                    controlDefinition.ControlTypeID != GridControlTypes.Link &&
                    controlDefinition.ControlTypeID != GridControlTypes.Command &&
                    controlDefinition.ControlTypeID != GridControlTypes.Image &&
                    controlDefinition.ControlTypeID != GridControlTypes.Delete &&
                    controlDefinition.ControlTypeID != GridControlTypes.Edit &&
                    controlDefinition.ControlTypeID != GridControlTypes.Update &&
                    controlDefinition.ControlTypeID != GridControlTypes.Cancel &&
                    controlDefinition.ControlTypeID != GridControlTypes.Add);
            if (editable) {
                return true;
            }
        }
        return false;
    }

    /**
     * Check if column is editable.
     * @param columnDefinition ColumnDefinition.
     * @param dataRow Current datarow.
     * @hidden
     */
    protected onColumnEditable(columnDefinition: IGridColumnDefinition, dataRow: IDataRow) {
        let isAdd = dataRow[DataConstants.ROW_INDEX_COLUMN_NAME] === -1;
        return this.isColumnEditable(columnDefinition, isAdd);
    }

    /**
     * Get the template for the dirtyField wrapper.
     * @param data Current dataRow.
     * @param fieldNames List of field names in a row.
     * @hidden
     */
    public dirtyFieldTemplate(data: any, fieldNames: string[]) {
        let rowIndex = Number(data[DataConstants.ROW_INDEX_COLUMN_NAME]);
        let changedValues = this.changedValues[rowIndex];
        if (changedValues) {
            for (let i = 0, len = fieldNames.length; i < len; i++) {
                let fieldName = fieldNames[i];
                if (changedValues.hasOwnProperty(fieldName)) {
                    return `<span class="k-dirty"></span>`;
                }
            }
        }
        return "";
    }

    /**
     * Modify the created column template.
     * @param template Default created template for the grid.
     * @param fieldNames All field names in this column.
     * @hidden
     */
    protected modifyColumnTemplate(template: string, fieldNames: string[]): string {
        if (!fieldNames || fieldNames.length == 0) {
            return template;
        }
        let dirtyTemplate = `#= this.dirtyFieldTemplate(data, ${JSON.stringify(fieldNames)}) # ${template}`;
        return <string>(<any>$.proxy(kendo.template(dirtyTemplate), this));
    }

    private addDependencies(dependencies: number[], controlDefinition: IControlDefinition) {
        if (controlDefinition.DependencyControlIds && controlDefinition.DependencyControlIds.length) {
            for (let dep of controlDefinition.DependencyControlIds) {
                if (dependencies.indexOf(dep) == -1) {
                    dependencies.push(dep);
                }

                let depControl = this.controlDefinitionDict[dep];
                if (depControl) {
                    this.addDependencies(dependencies, depControl);
                }
            }
        }
    }

    /**
     * This method is called for each column when entering edit/add mode of a row/cell.
     * @param columnDefinition Column.
     * @param container HTML-container where the controls should be added.
     * @param options Editor options, also includes the current datarow (options.model).
     * @hidden
     */
    protected getEditor(columnDefinition: IGridColumnDefinition, container: HTMLElement, options: kendo.ui.GridColumnEditorOptions): void {
        this.disposeEditContext();
        this.editContext.initialize(EditModes.Edit);

        let fieldNames: string[] = [];
        let dependencies: number[] = [];
        for (let i = 0, len = columnDefinition.Controls.length; i < len; i++) {
            let control = columnDefinition.Controls[i];

            if (control.ValueColumnName) {
                fieldNames.push(control.ValueColumnName);
            }
            // collect upward dependencies
            // -> all controls from which this column is dependent
            this.addDependencies(dependencies, control);
        }
        let dirtyFieldTemplate = this.dirtyFieldTemplate(options.model, fieldNames);
        if (dirtyFieldTemplate) {
            container.appendChild($(dirtyFieldTemplate)[0]);
        }
        super.getEditor(columnDefinition, container, options);

        console.time("initialize other controls");
        let hiddenControlContainer = $("<div style='display: none' />");
        hiddenControlContainer.appendTo(container);

        for (let i = 0, len = this.gridDefinition.Columns.length; i < len; i++) {
            let colDef = this.gridDefinition.Columns[i];
            if (colDef === columnDefinition) {
                continue;
            }

            let hasDependencyControls = false;
            for (let j = 0, ctrLen = colDef.Controls.length; j < ctrLen; j++) {
                if (
                    dependencies.indexOf(colDef.Controls[j].ControlID) != -1 ||
                    (colDef.Controls[j].DependencyControlIds && colDef.Controls[j].DependencyControlIds.length > 0)
                ) {
                    hasDependencyControls = true;
                    break;
                }
            }

            if (hasDependencyControls) {
                super.getEditor(colDef, hiddenControlContainer[0], options);
            }
        }
        console.timeEnd("initialize other controls");
    }

    /**
     * Reload the data of the grid.
     * Shows confirm page on unsaved changes.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // Reload the Grid with name GridName  on the page.
     * @@grd:GridName@@.reload();
     * ```
     */
    public async reload(): Promise<void> {
        let execute: boolean = true;
        if (this.hasChanges()) {
            execute = await Navigation.confirmAsync(
                this.textBlockService.get("EasyEditUnsavedChangesWindowUnloadHint", this.textBlockCategory),
                " "
            );
        }
        if (execute) {
            this.editFirstCell = true;
            this.clearChanges();
            await super.reload();
        }
    }

    /**
     * Returns true if the grid has unsaved changes.
     * @since 4.0.1
     * @example
     * ```JavaScript
     * @@grd:GridName@@.hasChanges()
     * ```
     */
    public hasChanges(): boolean {
        if (!this.columnSelectorColumnNames.length) {
            return Object.keys(this.changedRows).length > 0;
        }
        for (let rowIdx in this.changedValues) {
            if (this.changedValues.hasOwnProperty(rowIdx)) {
                let row = this.changedValues[rowIdx];

                for (let key in row) {
                    if (this.columnSelectorColumnNames.indexOf(key) == -1) {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    /**
     * Mark rows in all pages as dirty.
     * @since 4.0.2
     * @example
     * ```JavaScript
     * @@grd:GridName@@.setAllPagesDirty();
     * ```
     */
    public async setAllPagesDirty(): Promise<void> {
        this.setBusy(true);
        // 1. fetch all rows
        const result = await this.commandService.get(this.gridSettings.dataUrlGet, this.gridDefinition.GridID, null);

        // 2. add all rows to the changed rows collection.
        this.setRowsDirty(result.value);

        this.setBusy(false);
    }

    /**
     * Mark rows in current page as dirty.
     * @since 4.0.2
     * @example
     * ```JavaScript
     * @@grd:GridName@@.setCurrentPageDirty();
     * ```
     */
    public setCurrentPageDirty(): void {
        this.setRowsDirty(<IDataRow[]>(<any>this._kendoElement.dataSource.data()));
    }

    /**
     * Mark all items in given collection and child groups as dirty.
     * @param items Items to be marked as dirty.
     */
    private setRowsDirty(items: IDataRow[]): void {
        for (let i = 0, len = items.length; i < len; i++) {
            let item = items[i];
            if (item.aggregates != null && item.items != null) {
                this.setChangedValues(item.items);
            } else {
                let rowIndex = <string>item[DataConstants.ROW_INDEX_COLUMN_NAME];

                // add row to changed rows.
                if (!this.changedRows.hasOwnProperty(rowIndex)) {
                    this.changedRows[rowIndex] = item;
                }
            }
        }
    }

    /**
     * @hidden
     */
    protected onEdit(e: kendo.ui.GridEditEvent) {
        super.onEdit(e);
        if (this.tooltipExtension) {
            this.tooltipExtension.hide();
        }
        if (this.triggerCheckboxId != null) {
            let control = this.editContext.findControl(this.triggerCheckboxId);
            if (control != null) {
                control.getElement().click();
            }
        }
        this.triggerCheckboxId = null;
    }

    /**
     * Register specific events on this control.
     * @param control Control.
     * @hidden
     */
    protected registerControlEvents(control: IControl) {
        super.registerControlEvents(control);
        if (control.getControlType() == GridControlTypes.MultiPageUpdate) {
            control.on(ControlEventTypes.BeforeCommand, () => this.beforeMultipageUpdate());
            control.on(ControlEventTypes.GetChangedDataRows, e => this.onGetChangedDataRows(e));
            control.on(ControlEventTypes.AfterCommand, () => this.afterMultipageUpdate());
            control.on(ControlEventTypes.AfterCommandCancel, () => this.afterCommandCancel());
        }
    }

    private beforeMultipageUpdate() {
        this.setBusy(true);
        this._saveScrollPos = true;
        this.saveScrollPosition();
    }

    /**
     *
     * @param control
     * @param cssClass
     * @hidden
     */
    protected setRowControlEvent(control: IControl, cssClass: string) {
        if (cssClass == "." + CssClass.link) {
            if (control.getControlDefinition().Script && control.getControlDefinition().Script.indexOf("$dms") >= 0) {
                control.on(ControlEventTypes.BeforeCommand, e => this.openRowForEdit(e));
                control.on(ControlEventTypes.AfterCommand, e => this.closeRowForEdit(e));
            }
        }
    }

    private openRowForEdit(e: EventArgs<IControl>): void {
        let dataRow = this.getDataRow(e.sender);
        let uid = (<any>dataRow).uid;
        let cell = this.jQueryElement.find("tr[data-uid=" + uid + "] .dms-column-editable:first");
        if (cell) {
            this._kendoElement.editCell(cell);
        }
    }

    private closeRowForEdit(e: EventArgs<IControl>): void {
        // let dataRow = this.getDataRow(e.sender);
        // this._kendoElement.saveChanges();
    }

    private afterMultipageUpdate(): void {
        this.setBusy(false);
        this.notifyIfEnabled(this.textBlockService.get("NotificationEditRowSuccess", TextBlockCategory.global), NotificationTypes.Success);
        this.clearChanges();
        this.reload();
    }

    /**
     * Clear all changes internally to prevent the navigation hint.
     * **Does not reload the grid or change the values back to the original values**.
     * Use `reload` to reload the data.
     * @since 4.1.0
     * @example
     * ```JavaScript
     * @@grd:GridName@@.clearChanges();
     * ```
     */
    public clearChanges() {
        this.changedRows = {};
        this.changedValues = {};
        this.originalValues = {};
    }

    private onGetChangedDataRows(e: EventArgs<IControl>): IDataRow[] {
        let rows: IDataRow[] = [];

        if (this.hasChanges()) {
            for (let key in this.changedRows) {
                if (this.changedRows.hasOwnProperty(key)) {
                    rows.push(this.changedRows[key]);
                }
            }
        }
        return rows;
    }

    private triggerCheckboxId: number;

    /**
     * @hidden
     */
    protected onDataBound(e: kendo.ui.GridDataBoundEvent) {
        super.onDataBound(e);

        let cell = this.jQueryElement.find("td.dms-column-editable:first");
        if (cell) {
            if (this._kendoElement && this.editFirstCell) {
                this._saveScrollPos = true;
                this.saveScrollPosition();
                this._kendoElement.editCell(cell);
            }
            this.editFirstCell = false;
        }
    }

    /**
     * Event occurs before grid data was bound.
     * @param e EventArgs.
     * @hidden
     */
    protected onDataBinding(e: kendo.ui.GridDataBindingEvent) {
        // all items for the current page
        if (e.action == "rebind") {
            if (Object.keys(this.changedValues).length > 0 && e.items) {
                this.setChangedValues(e.items);
            }
        }
        super.onDataBinding(e);
    }

    private setChangedValues(items: any) {
        for (let i = 0, len = items.length; i < len; i++) {
            let item = items[i];
            if (item.aggregates != null && item.items != null) {
                this.setChangedValues(item.items);
            } else {
                let rowIndex = item[DataConstants.ROW_INDEX_COLUMN_NAME];

                // find the current row in the changedValues and copy all properties to the original row.
                if (this.changedValues.hasOwnProperty(rowIndex)) {
                    let changedItem = this.changedValues[rowIndex];
                    for (let prop in changedItem) {
                        item.set(prop, changedItem[prop]);
                    }
                }
            }
        }
    }

    /**
     * Dispose the grid and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        if (this.jQueryElement) {
            this.jQueryElement.off("beforeunload");
        }
    }
}
